<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const isProcessing = ref(false);

let conversionWorker = null;
let workerIdCounter = 0;
let audioContext = null;

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

const decodeAudioData = async audioBlob => {
  const ctx = getAudioContext();
  const arrayBuffer = await audioBlob.arrayBuffer();
  return ctx.decodeAudioData(arrayBuffer);
};

const initConversionWorker = () => {
  conversionWorker = new Worker(
    new URL('./utils/audioConversionWorker.js', import.meta.url),
    { type: 'module' }
  );

  conversionWorker.onmessage = event => {
    const { id, blob, success, error } = event.data;
    isProcessing.value = false;

    if (success) {
      const extension = props.audioRecordFormat === 'audio/wav' ? 'wav' : 'mp3';
      const fileName = `${getUuid()}.${extension}`;
      const file = new File([blob], fileName, { type: props.audioRecordFormat });
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
    }
    isRecording.value = false;
  };
};

const handleRecordEnd = async blob => {
  const audioUrl = URL.createObjectURL(blob);
  wavesurfer.value.load(audioUrl);
  hasRecording.value = true;
  isProcessing.value = true;

  try {
    const audioBuffer = await decodeAudioData(blob);
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;

    const channelBuffers = [];
    const transferables = [];
    for (let c = 0; c < numberOfChannels; c += 1) {
      const src = audioBuffer.getChannelData(c);
      const copy = new Float32Array(src);
      channelBuffers.push(copy.buffer);
      transferables.push(copy.buffer);
    }

    const id = ++workerIdCounter;
    conversionWorker.postMessage(
      {
        id,
        channelBuffers,
        numberOfChannels,
        sampleRate,
        format: props.audioRecordFormat,
        bitrate: 128,
      },
      transferables
    );
  } catch (error) {
    isProcessing.value = false;
    isRecording.value = false;
  }
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', handleRecordEnd);

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initConversionWorker();
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (conversionWorker) {
    conversionWorker.terminate();
  }
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
  if (audioContext) {
    audioContext.close();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div class="w-full">
    <div ref="waveformContainer" class="w-full p-1" />
    <div
      v-if="isProcessing"
      class="flex justify-center items-center py-1 text-xs text-slate-500"
    >
      Processing audio...
    </div>
  </div>
</template>
